import { MedChronSourceRef, MedChronSourceRefSchema } from '@/medical-chronology/sources/schemas'
import { parseISODate } from '@/util/zod/validators'
import { z } from 'zod'

// A single event field value paired with its reference id
export const EventFieldItemSchema = <T extends z.ZodTypeAny>(valueSchema: T) =>
  z.object({
    reference_id: z.string().uuid(),
    value: valueSchema,
  })

export type EventFieldItem<T> = {
  reference_id: string
  value: T
}

// UI-ready event field item
export type UIEventFieldItem = {
  reference_id: string
  text: string
}

// UI-ready medical chronology event
export type UIMedChronEvent = {
  id: string
  dateOfService: UIEventFieldItem
  facility: UIEventFieldItem
  provider: UIEventFieldItem
  summarySentences: UIEventFieldItem[]
  diagnoses: UIEventFieldItem[]
  icd10Codes: UIEventFieldItem[]
  testsAndOrImaging: UIEventFieldItem[]
  procedures: UIEventFieldItem[]
  medications: UIEventFieldItem[]
  source: MedChronSourceRef
  pages: number[]
}

export const MedChronEventSchema = z.object({
  id: z.string().uuid(),
  date_of_service: EventFieldItemSchema(z.preprocess((val, ctx) => parseISODate(val, ctx), z.string())),
  facility: EventFieldItemSchema(z.string()),
  providers: z.array(EventFieldItemSchema(z.string())),
  summary_sentences: z.array(EventFieldItemSchema(z.string())),
  file_path: z.string(),
  source: MedChronSourceRefSchema,
  // Backend emits 1-based page numbers (PaxtonHighlightBlock.page_number is documented "Canonical
  // 1-based document page number"), but the shared viewer layer is 0-based: PageCitation renders
  // `page + 1` for its label and the PDF viewer's jumpToPage is 0-indexed. Convert to 0-based here
  // so the source-file page chips read the true page and click-to-source lands on it. Mirrors the
  // note in references/store/schemas.ts and the same fix in timeline/events/store/schemas.ts.
  pages: z.array(z.number().int().nonnegative()).transform((pages) => pages.map((page) => page - 1)),
  diagnoses: z.array(EventFieldItemSchema(z.string())),
  icd_10_codes: z.array(EventFieldItemSchema(z.string())),
  tests_and_or_imaging: z.array(EventFieldItemSchema(z.string())),
  procedures: z.array(EventFieldItemSchema(z.string())),
  medications: z.array(EventFieldItemSchema(z.string())),
})

export type MedChronEvent = z.infer<typeof MedChronEventSchema>
